import { useCallback, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { Dependency, useDependencyContext } from './DependencyContext'
import { useDependencyDragInteraction } from './useDependencyDragInteraction'
import { DepPopupState, useDependencyLinePopup } from './useDependencyLinePopup'
import { categorizeLinks, FallbackLinkInfo } from '../utils/visibilityFilters'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'
import { WbsItemLinkSummary } from '../WbsItemLink/WbsItemLinkDialog'

type UseGanttDependencyInteractionParams = {
  api: GridApi
  dataPath?: string
  rowIndex: number
  isTask: boolean
  isDeliverable: boolean
  barEndpoints: { startX: number | null; endX: number | null }
  isHovered: boolean
  isRowHovered: boolean
}

type DepListDialogState = {
  items: WbsItemLinkSummary[]
}

type UseGanttDependencyInteractionResult = {
  dependencies: Dependency[]
  partialDeps: FallbackLinkInfo<Dependency>[]
  fallbackDeps: FallbackLinkInfo<Dependency>[]
  isDependencyDragging: boolean
  isPotentialDropTarget: boolean
  showLeftDropTarget: boolean
  showSourceCircles: boolean
  handleConnectionMouseDown: (
    event: React.MouseEvent,
    side: 'start' | 'end'
  ) => void
  handleDropTargetMouseUp: (event: React.MouseEvent) => void
  depPopup: DepPopupState | null
  handleDependencyLineClick: (dep: Dependency, event: React.MouseEvent) => void
  handleDeleteDependency: () => void
  handleDependencyUpdate: (result: LinkEditorResult) => void
  closeDependencyPopup: () => void
  handleFallbackDepClick: (
    info: FallbackLinkInfo<Dependency>,
    event: React.MouseEvent
  ) => void
  depListDialog: DepListDialogState | null
  handleFallbackDepGroupClick: (
    infos: FallbackLinkInfo<Dependency>[],
    event: React.MouseEvent
  ) => void
  handleDepDialogUpdate: (
    uuid: string,
    updates: { color?: string; description?: string }
  ) => void
  handleDepDialogRemove: (uuid: string) => void
  closeDepListDialog: () => void
}

export const useGanttDependencyInteraction = (
  params: UseGanttDependencyInteractionParams
): UseGanttDependencyInteractionResult => {
  const {
    api,
    dataPath,
    rowIndex,
    isTask,
    isDeliverable,
    barEndpoints,
    isHovered,
    isRowHovered,
  } = params
  const prefix = dataPath ? `${dataPath}.` : ''

  // Dependency context: categorize into visible lines, partial lines, and fallback icons
  const dependencyContext = useDependencyContext()
  const {
    visible: dependencies,
    partial: partialDeps,
    fallback: fallbackDeps,
  } = categorizeLinks(dependencyContext?.dependencies ?? [], api)

  // Dependency item type for this row
  const dependencyItemType = isTask
    ? 'task'
    : isDeliverable
      ? 'deliverable'
      : null

  // Dependency drag interaction
  const {
    isDependencyDragging,
    isPotentialDropTarget,
    showLeftDropTarget,
    showSourceCircles,
    handleConnectionMouseDown,
    handleDropTargetMouseUp,
  } = useDependencyDragInteraction({
    rowIndex,
    dependencyItemType,
    barEndpoints,
    isHovered,
    isRowHovered,
  })

  // Dependency line popup
  const {
    depPopup,
    handleLineClick: handleDependencyLineClick,
    handleDelete: handleDeleteDependency,
    handleUpdate: handleDependencyUpdate,
    closePopup: closeDependencyPopup,
    openPopup: openDepPopup,
  } = useDependencyLinePopup(api, dataPath)

  // WBS item info map from context (server-sourced, avoids full-grid iteration)
  const depWbsItemInfoMap = dependencyContext?.wbsItemInfoMap

  // Fallback icon click handler (resolve names/icons for hidden endpoints)
  const handleFallbackDepClick = useCallback(
    (info: FallbackLinkInfo<Dependency>, event: React.MouseEvent) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      if (from.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
      }
      let toName: string
      let toIconUrl: string | undefined
      if (to.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
      }
      openDepPopup({
        dependency: link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
      })
    },
    [api, prefix, openDepPopup, depWbsItemInfoMap]
  )

  // ============================================================================
  // List dialog for grouped fallback icons
  // ============================================================================
  const [depListDialog, setDepListDialog] = useState<DepListDialogState | null>(
    null
  )

  const resolveDepName = useCallback(
    (info: FallbackLinkInfo<Dependency>) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      if (from.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
      }
      let toName: string
      let toIconUrl: string | undefined
      if (to.isFallback) {
        const resolved = depWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
      }
      return { fromName, fromIconUrl, toName, toIconUrl }
    },
    [api, prefix, depWbsItemInfoMap]
  )

  const handleFallbackDepGroupClick = useCallback(
    (infos: FallbackLinkInfo<Dependency>[]) => {
      const items: WbsItemLinkSummary[] = infos.map(info => {
        const { fromName, fromIconUrl, toName, toIconUrl } =
          resolveDepName(info)
        return {
          uuid: info.link.uuid,
          fromWbsItemUuid: info.link.fromWbsItemUuid,
          toWbsItemUuid: info.link.toWbsItemUuid,
          fromName,
          toName,
          fromIconUrl,
          toIconUrl,
          color: info.link.color ?? null,
          description: info.link.description ?? null,
          createdAt: info.link.createdAt,
          createdBy: info.link.createdBy,
          updatedAt: info.link.updatedAt,
          updatedBy: info.link.updatedBy,
        }
      })
      setDepListDialog({ items })
    },
    [resolveDepName]
  )

  const handleDepDialogUpdate = useCallback(
    (uuid: string, updates: { color?: string; description?: string }) => {
      dependencyContext?.updateDependencyLink(uuid, updates)
      setDepListDialog(prev => {
        if (!prev) return null
        return {
          items: prev.items.map(item =>
            item.uuid === uuid
              ? {
                  ...item,
                  color: updates.color ?? item.color,
                  description: updates.description ?? item.description,
                }
              : item
          ),
        }
      })
    },
    [dependencyContext]
  )

  const handleDepDialogRemove = useCallback(
    (uuid: string) => {
      dependencyContext?.removeDependencyByUuid(uuid)
      setDepListDialog(prev => {
        if (!prev) return null
        const remaining = prev.items.filter(item => item.uuid !== uuid)
        if (remaining.length === 0) return null
        return { items: remaining }
      })
    },
    [dependencyContext]
  )

  const closeDepListDialog = useCallback(() => {
    setDepListDialog(null)
  }, [])

  return {
    dependencies,
    partialDeps,
    fallbackDeps,
    isDependencyDragging,
    isPotentialDropTarget,
    showLeftDropTarget,
    showSourceCircles,
    handleConnectionMouseDown,
    handleDropTargetMouseUp,
    depPopup,
    handleDependencyLineClick,
    handleDeleteDependency,
    handleDependencyUpdate,
    closeDependencyPopup,
    handleFallbackDepClick,
    depListDialog,
    handleFallbackDepGroupClick,
    handleDepDialogUpdate,
    handleDepDialogRemove,
    closeDepListDialog,
  }
}
